/*
 * Gráfica de barras horizontales apiladas del tablero (semáforo), sobre el
 * motor unificado de gráficas (Echartxx de codekit, FASE 5). La leyenda
 * clickable que oculta segmentos y recalcula la escala es nativa de
 * ECharts. Reemplaza la réplica artesanal de Chart.js en CSS.
 */
import { useMemo } from "react";

import { Echartxx } from "codekit";

import { bucketColors, fmtCOP, htmlTheme } from "./utils/carthelp";

const SEGMENTS = ["verde", "amarillo", "rojo"];

/**
 * Renderiza una gráfica de barras apiladas con leyenda, ejes y gridlines
 * @param {Object} props Propiedades del componente
 * @param {Array} props.data Filas [{ label, raw, verde, amarillo, rojo, total }]
 * @param {Function} props.onItemClick Callback con la fila y su índice al hacer click
 * @param {Array} props.legend Etiquetas de la leyenda [verde, amarillo, rojo]
 * @param {number} props.height Alto total del área de la gráfica
 */
const Stakbars = ({ data = [], onItemClick, legend = ["Verde", "Amarillo", "Rojo"], height = 400 }) => {
  const bClickable = typeof onItemClick === "function";

  //ECharts pinta las categorías de abajo hacia arriba: se invierte para
  //conservar el orden visual (primera fila arriba).
  const mFilas = useMemo(() => [...data].reverse(), [data]);

  const opcion = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        valueFormatter: (nValor) => fmtCOP(nValor),
      },
      legend: { top: 0, icon: "roundRect", textStyle: { fontSize: 12, color: htmlTheme.muted } },
      grid: { left: 8, right: 24, top: 32, bottom: 4, containLabel: true },
      xAxis: {
        type: "value",
        axisLabel: { fontSize: 10, color: htmlTheme.muted, formatter: (nValor) => fmtCOP(nValor) },
      },
      yAxis: {
        type: "category",
        data: mFilas.map((mItem) => mItem.label),
        axisLabel: { fontSize: 11, color: htmlTheme.muted, overflow: "truncate", width: 170 },
      },
      series: SEGMENTS.map((sSegmento, nIndice) => ({
        name: legend[nIndice],
        type: "bar",
        stack: "semaforo",
        cursor: bClickable ? "pointer" : "default",
        barMaxWidth: 26,
        itemStyle: { color: bucketColors[sSegmento] },
        emphasis: { itemStyle: { opacity: 0.85 } },
        data: mFilas.map((mItem) => mItem[sSegmento] ?? 0),
      })),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mFilas, legend],
  );

  const eventos = useMemo(
    () => ({
      click: (params) => {
        const mItem = mFilas[params.dataIndex];
        if (bClickable && mItem !== undefined) {
          onItemClick(mItem, data.indexOf(mItem));
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mFilas, onItemClick],
  );

  return <Echartxx option={opcion} onEvents={eventos} height={height} />;
};

export default Stakbars;
